import { camelCase, snakeCase } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';

const FORM_DEFAULT_VALUES = {
    automationEnabled: false,
};

export const UniversalDocumentConfig = ({ config, onSubmit }) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const documentConfig = React.useMemo(() => {
        return config ? Object.fromEntries(config.options.map((option) => [camelCase(option.name), option.value])) : {};
    }, [config]);

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        form.reset({
            automationEnabled: documentConfig?.automationEnabled,
        });
    }, [config]);

    const handleSubmit = async (formData) => {
        try {
            await onSubmit({
                options: Object.entries(formData).map(([optionName, optionValue]: any) => ({
                    name: snakeCase(optionName),
                    value: optionValue,
                })),
            });
            publishToast({ description: t('configPage.document.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.document.updateError'), status: 'error' });
        }
    };

    return (
        <SettingsFormCard
            onSubmit={form.handleSubmit(handleSubmit)}
            title={t('configPage.document.generalSections.automation')}
        >
            <Controller
                name="automationEnabled"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.document.form.automationEnabled')}
                        error={formErrors.automationEnabled}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <SaveStatus
                isDirty={form.formState.isDirty}
                isSubmitting={form.formState.isSubmitting}
                errorCount={countFieldErrors(form.formState.errors)}
            />
        </SettingsFormCard>
    );
};
